import { useAuthRedux } from '@hooks/useAuthRedux';
import { useUser } from '@hooks/useUser';
import Divider from '@material-ui/core/Divider';
import Icon from '@material-ui/core/Icon';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import { UserType } from '@model/User';
import { createMemo } from '@packages/react';
import { buildVariants } from '@packages/react/lib/buildVariants';
import { IF } from '@packages/react/lib/if';
import React, { useEffect, useState } from 'react';
import { Link } from './Link';
import { BaseMenu } from './Menus/BaseMenu';
import { ProfilePicture } from './ProfilePicture';

const VARIANTS_OBJECT = {
    default: (user: UserType) => (
        <Tooltip title={user.$fullName()} placement={'bottom'}>
            <ProfilePicture variant={'user'} data={user} />
        </Tooltip>
    ),

    action: (user: UserType) => (
        <BaseMenu
            variant={'custom'}
            toggle={<ProfilePicture variant={'user'} data={user} />}
            disableAutoFocusItem
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            style={{ maxWidth: 300 }}
        >
            {VARIANTS.profile(user)}

            <Divider style={{ userSelect: 'none', margin: '8px 0' }} />
            {IF(
                user.user_view === 'customer',
                <MenuItem component={Link} to={'/wallet'}>
                    <ListItemIcon>
                        <Icon>account_balance_wallet</Icon>
                    </ListItemIcon>
                    <ListItemText primary={'Your Wallet'} />
                </MenuItem>,
            )}
            <MenuItem component={Link} to={'Account.Settings'}>
                <ListItemIcon>
                    <Icon>settings</Icon>
                </ListItemIcon>
                <ListItemText primary={'Settings'} />
            </MenuItem>

            <MenuItem component={Link} to={'auth.logout'}>
                <ListItemIcon>
                    <Icon>exit_to_app</Icon>
                </ListItemIcon>
                <ListItemText primary={'Sign Out'} />
            </MenuItem>
        </BaseMenu>
    ),

    profile: (user: UserType) => (
        <ListItem>
            <ListItemAvatar>
                <ProfilePicture variant={'user'} data={user} />
            </ListItemAvatar>
            <ListItemText
                primary={user.$fullName()}
                secondary={user.email}
                primaryTypographyProps={{
                    title: user.$fullName(),
                    className: 'user__full-name',
                    variant: 'body2',
                    noWrap: true,
                }}
                secondaryTypographyProps={{
                    title: user.email,
                    className: 'user__email',
                    variant: 'body1',
                    noWrap: true,
                }}
            />
        </ListItem>
    ),
};

const VARIANTS = buildVariants(VARIANTS_OBJECT);

interface CurrentUserProps {
    variant?: keyof typeof VARIANTS;
}

export const CurrentUser = createMemo<CurrentUserProps>(
    (props) => {
        // debugger;
        const { variant, ...rest } = props;

        const [auth] = useAuthRedux();
        const user: UserType = useUser();

        const hasUser = !!(auth && user);

        const variant$ = VARIANTS.getVariant(variant);
        const [children, setChildren] = useState(user ? variant$(user, rest) : null);

        const user$ = (user || {}) as UserType;

        const uniqData = [user$.$fullName ? user$.$fullName() : '', user$.thumbnail || ''].join('_');

        useEffect(() => {
            if (user) {
                setChildren(variant$(user, rest));
            }
        }, [user$.id, uniqData]);

        return IF(hasUser, children);
    },
    {
        displayName: 'CurrentUser',
        defaultProps: {
            variant: VARIANTS.getDefaultVariant(),
        },
    },
);
